import { ButtonPrimitive, CardPrimitive, Chip as FplChip } from '@figma/fpl-components';
import { Icon16Close } from '@figma/fpl-icons';
import { borderRadius, color as fplColor, spacer } from '@figma/fpl-tokens/tokens.stylex';
import { Chip as MuiChip, type ChipProps as MuiChipProps } from '@mui/material';
import * as stylex from '@stylexjs/stylex';
import { forwardRef, useCallback, useId, type ComponentProps, type KeyboardEvent, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { I18N_KEYS } from '@/language/keys';
import { typographyStyles } from '@/UI/AppTypography/AppTypography';
import { getFplDataAttributes } from '@/UI/Buttons/fplDataAttributes';

type FplChipProps = ComponentProps<typeof FplChip>;

export type ChipFplProps = Pick<FplChipProps, 'selected'>;

export type ChipProps = MuiChipProps & {
  /** Explicit FPL-only configuration. Ignored by the legacy MUI branch. */
  fplProps?: ChipFplProps;
};

const styles = stylex.create({
  root: {
    '--fpl-icon-color': fplColor.textSecondary,
    alignItems: 'center',
    backgroundColor: fplColor.bg,
    borderRadius: borderRadius.medium,
    boxShadow: `inset 0 0 0 1px ${fplColor.border}`,
    color: fplColor.text,
    display: 'inline-flex',
    flex: '0 0 auto',
    isolation: 'isolate',
    maxWidth: '100%',
    outline: {
      default: 'none',
      ':has(:focus-visible)': `1px solid ${fplColor.borderSelected}`,
    },
    outlineOffset: {
      default: 0,
      ':has(:focus-visible)': '1px',
    },
    overflow: 'hidden',
    position: 'relative',
    userSelect: 'none',
    width: 'fit-content',
  },
  small: {
    borderRadius: '3px',
    height: 'calc(20rem / 16)',
  },
  medium: {
    height: spacer.spacer24px,
  },
  primaryInteractive: {
    '--fpl-icon-color': {
      default: fplColor.textSecondary,
      ':hover': fplColor.text,
      ':has(:focus-visible)': fplColor.text,
    },
    backgroundColor: {
      default: fplColor.bg,
      ':hover': fplColor.bgTransparentHover,
      ':active': fplColor.bgTransparentPressed,
    },
  },
  primarySelected: {
    backgroundColor: {
      default: fplColor.bgSelected,
      ':hover': fplColor.bgSelected,
      ':active': fplColor.bgSelectedPressed,
    },
    boxShadow: `inset 0 0 0 1px ${fplColor.borderSelected}`,
    color: fplColor.textSelected,
    '--fpl-icon-color': fplColor.textSelected,
  },
  disabled: {
    backgroundColor: fplColor.bg,
    boxShadow: `inset 0 0 0 1px ${fplColor.borderDisabled}`,
    color: fplColor.textDisabled,
    '--fpl-icon-color': fplColor.textDisabled,
  },
  disabledSelected: {
    backgroundColor: fplColor.bgSecondary,
    boxShadow: `inset 0 0 0 1px ${fplColor.border}`,
  },
  customInteractive: (borderColor: string) => ({
    boxShadow: {
      default: `inset 0 0 0 1px ${borderColor}`,
      ':hover': `inset 0 0 0 1px ${borderColor}, inset 0 0 0 9999px ${fplColor.bgTransparentHover}`,
      ':active': `inset 0 0 0 1px ${borderColor}, inset 0 0 0 9999px ${fplColor.bgTransparentPressed}`,
    },
  }),
  customBackground: (background: string) => ({
    background,
  }),
  customForeground: (foreground: string) => ({
    '--fpl-icon-color': foreground,
    color: foreground,
  }),
  customBorder: (borderColor: string) => ({
    boxShadow: `inset 0 0 0 1px ${borderColor}`,
  }),
  content: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
    width: '100%',
  },
  title: {
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  titleSmall: {
    paddingInline: spacer.spacer4px,
  },
  titleMedium: {
    paddingInline: spacer.spacer8px,
  },
  titleWithTrailing: {
    paddingRight: 0,
  },
  mainButton: {
    outline: 'none',
  },
  closeButtonWrapper: {
    alignItems: 'center',
    display: 'flex',
    flex: '0 0 auto',
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative',
  },
  closeButtonSmall: {
    maxWidth: 'calc(20rem / 16)',
  },
  closeButtonMedium: {
    maxWidth: spacer.spacer24px,
  },
  closeButton: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    opacity: {
      default: 1,
      ':active': 0.5,
    },
    outline: 'none',
  },
  closeButtonSizeSmall: {
    height: 'calc(20rem / 16)',
    width: 'calc(20rem / 16)',
  },
  closeButtonSizeMedium: {
    height: spacer.spacer24px,
    width: spacer.spacer24px,
  },
});

/**
 * Reads only existing root color overrides. Other MUI `sx` values, nested
 * selectors, and theme functions are intentionally opaque to the FPL
 * implementation.
 */
type CustomChipColors = {
  background?: string;
  border?: string;
  foreground?: string;
};

function getSxColors(sx: unknown): CustomChipColors {
  if (Array.isArray(sx)) {
    return sx.reduce<CustomChipColors>((colors, entry: unknown) => ({ ...colors, ...getSxColors(entry) }), {});
  }

  if (sx == null || typeof sx !== 'object') return {};

  const background =
    'background' in sx && typeof sx.background === 'string'
      ? sx.background
      : 'backgroundColor' in sx && typeof sx.backgroundColor === 'string'
        ? sx.backgroundColor
        : undefined;
  const border = 'borderColor' in sx && typeof sx.borderColor === 'string' ? sx.borderColor : undefined;
  const foreground = 'color' in sx && typeof sx.color === 'string' ? sx.color : undefined;

  return {
    ...(background != null ? { background } : null),
    ...(border != null ? { border } : null),
    ...(foreground != null ? { foreground } : null),
  };
}

/**
 * Feature-flag boundary for Weave chips. The enabled branch mirrors FPL Chip's
 * CardPrimitive composition and StyleX-translates its visual contract, adding
 * support for caller-owned background, foreground, and border colors.
 */
export const Chip = forwardRef<HTMLDivElement, ChipProps>(function Chip(
  {
    'aria-controls': ariaControls,
    'aria-description': ariaDescription,
    'aria-expanded': ariaExpanded,
    'aria-haspopup': ariaHasPopup,
    'aria-label': ariaLabel,
    color,
    deleteIcon,
    disabled,
    fplProps,
    id,
    label,
    onBlur,
    onClick,
    onDelete,
    onDoubleClick,
    onFocus,
    onKeyDown,
    onKeyUp,
    onMouseEnter,
    onMouseLeave,
    role,
    size,
    sx,
    title,
    variant,
    ...legacyProps
  },
  ref,
) {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const { t } = useTranslation();
  const titleId = useId();

  const isInteractive = onDelete != null || onClick != null;
  const isSmall = size === 'small';
  const labelText = typeof label === 'string' || typeof label === 'number' ? String(label) : ariaLabel;
  const resolvedDeleteLabel = t(I18N_KEYS.GENERAL.ARIA.REMOVE_NAMED_ITEM, {
    name: labelText ?? t(I18N_KEYS.SHARE_WORKFLOW_MODAL.REMOVE),
  });
  const resolvedDeleteDescription = t(I18N_KEYS.GENERAL.ARIA.REMOVE_NAMED_ITEM_KEYBOARD, {
    name: labelText ?? t(I18N_KEYS.SHARE_WORKFLOW_MODAL.REMOVE),
  });

  const handlePrimitiveKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;

      if (onDelete && (event.key === 'Backspace' || event.key === 'Delete')) {
        onDelete(event);
        event.preventDefault();
        event.stopPropagation();
      }

      onKeyDown?.(event);
    },
    [disabled, onDelete, onKeyDown],
  );

  const handleClose = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onDelete?.(event);
    },
    [onDelete],
  );

  if (isFplMigrationEnabled !== true) {
    return (
      <MuiChip
        {...legacyProps}
        ref={ref}
        aria-controls={ariaControls}
        aria-description={ariaDescription}
        aria-expanded={ariaExpanded}
        aria-haspopup={ariaHasPopup}
        aria-label={ariaLabel}
        color={color}
        deleteIcon={deleteIcon}
        disabled={disabled}
        id={id}
        label={label}
        onBlur={onBlur}
        onClick={onClick}
        onDelete={onDelete}
        onDoubleClick={onDoubleClick}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        role={role}
        size={size}
        sx={sx}
        title={title}
        variant={variant}
      />
    );
  }

  const sxColors = getSxColors(sx);
  const selected = fplProps?.selected ?? false;
  const background = !selected && !disabled ? sxColors.background : undefined;
  const border = !selected && !disabled ? sxColors.border : undefined;
  const foreground = !selected && !disabled ? sxColors.foreground : undefined;
  const usesCustomBackground = background != null;
  const mainActionDescription = ariaDescription ?? (onDelete ? resolvedDeleteDescription : undefined);

  return (
    <CardPrimitive.Root
      {...getFplDataAttributes(legacyProps)}
      {...stylex.props(
        styles.root,
        typographyStyles.textBodyMedium,
        isSmall ? styles.small : styles.medium,
        !usesCustomBackground && isInteractive && !selected && !disabled ? styles.primaryInteractive : null,
        selected && styles.primarySelected,
        disabled && styles.disabled,
        disabled && selected && styles.disabledSelected,
        usesCustomBackground && styles.customBackground(background),
        foreground != null && styles.customForeground(foreground),
        border != null && styles.customBorder(border),
        usesCustomBackground && isInteractive ? styles.customInteractive(border ?? fplColor.border) : null,
      )}
      data-fpl-component=""
      ref={ref}
      id={id}
      onBlur={onBlur}
      onClick={disabled ? undefined : onClick}
      onDoubleClick={onDoubleClick}
      onFocus={onFocus}
      onKeyDown={handlePrimitiveKeyDown}
      onKeyUp={onKeyUp}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      role={role}
      title={title}
    >
      {isInteractive ? (
        <CardPrimitive.MainButton
          {...stylex.props(styles.mainButton)}
          data-fpl-component=""
          aria-controls={ariaControls}
          aria-description={mainActionDescription}
          aria-expanded={ariaExpanded}
          aria-haspopup={ariaHasPopup}
          aria-label={ariaLabel}
          disabled={disabled}
        />
      ) : null}
      <div {...stylex.props(styles.content)}>
        <span
          {...stylex.props(
            styles.title,
            isSmall ? styles.titleSmall : styles.titleMedium,
            onDelete && styles.titleWithTrailing,
          )}
        >
          {isInteractive ? (
            ariaLabel ? (
              <span aria-hidden="true">{label}</span>
            ) : (
              <CardPrimitive.Title id={titleId} as="span">
                {label ?? ''}
              </CardPrimitive.Title>
            )
          ) : (
            <span>{label}</span>
          )}
        </span>
        {onDelete ? (
          <CardPrimitive.Interactive
            {...stylex.props(styles.closeButtonWrapper, isSmall ? styles.closeButtonSmall : styles.closeButtonMedium)}
          >
            <ButtonPrimitive
              {...stylex.props(
                styles.closeButton,
                isSmall ? styles.closeButtonSizeSmall : styles.closeButtonSizeMedium,
              )}
              data-fpl-component=""
              aria-describedby={ariaLabel ? undefined : titleId}
              aria-label={resolvedDeleteLabel}
              disabled={disabled}
              onClick={handleClose}
              onDoubleClick={(event) => event.stopPropagation()}
              tabIndex={-1}
            >
              <Icon16Close />
            </ButtonPrimitive>
          </CardPrimitive.Interactive>
        ) : null}
      </div>
    </CardPrimitive.Root>
  );
});
